import { useState, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import { validateVideoFile } from "@/lib/video-utils";
import { convertVideoInBrowser } from "@/lib/video-conversion";

interface VideoUploadResult {
  gif_key: string;
  original_filename: string;
}

interface UseVideoUploadOptions {
  maxFileSize?: number;
  allowedTypes?: string[];
  reffId?: string;
  onSuccess?: (result: VideoUploadResult) => void;
  onError?: (error: string) => void;
}

interface UseVideoUploadReturn {
  upload: (file: File) => Promise<VideoUploadResult>;
  isUploading: boolean;
  conversionProgress: number;
  uploadProgress: number;
  error: string | null;
  reset: () => void;
}

export function useVideoUpload(
  options: UseVideoUploadOptions = {}
): UseVideoUploadReturn {
  const {
    maxFileSize = 100 * 1024 * 1024, // 100MB default
    allowedTypes = [
      "video/mp4",
      "video/quicktime",
      "video/webm",
      "video/x-msvideo",
    ],
    reffId,
    onSuccess,
    onError,
  } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // use convex r2 upload mutation for video files
  const generateVideoUploadUrl = useMutation(api.r2.generateVideoUploadUrl);

  const validateFile = useCallback(
    (file: File): string | null => {
      const validation = validateVideoFile(file, {
        maxSize: maxFileSize,
        allowedTypes,
      });
      if (!validation.isValid) {
        return validation.error || "invalid video file";
      }
      return null;
    },
    [maxFileSize, allowedTypes]
  );

  const uploadToConvex = useCallback(
    async (file: File, extension: string): Promise<string> => {
      if (!reffId) {
        throw new Error("reffId is required for uploading videos");
      }

      // get upload url from convex
      const { url, key } = await generateVideoUploadUrl({
        reffId,
        fileExtension: extension as ".webm" | ".gif" | ".mp4" | ".mov" | ".avi",
      });

      // upload file to r2
      const uploadResponse = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error(`upload failed with status: ${uploadResponse.status}`);
      }

      return key;
    },
    [generateVideoUploadUrl, reffId]
  );

  const upload = useCallback(
    async (file: File): Promise<VideoUploadResult> => {
      setIsUploading(true);
      setError(null);
      setConversionProgress(0);
      setUploadProgress(0);

      try {
        // validate file
        const validationError = validateFile(file);
        if (validationError) {
          throw new Error(validationError);
        }

        if (!reffId) {
          throw new Error("reffId is required for uploading videos");
        }

        // step 1: convert video to gif in browser (0-70% progress)
        setConversionProgress(0);
        setUploadProgress(0);
        const { gifFile } = await convertVideoInBrowser(file, (progress) => {
          setConversionProgress(progress * 0.7); // conversion is 0-70%
        });
        setConversionProgress(70);
        setUploadProgress(20);

        // step 2: upload gif to R2 (70-100% progress)
        setUploadProgress(30);
        const gifKey = await uploadToConvex(gifFile, ".gif");
        setUploadProgress(90);
        setConversionProgress(90);

        // step 3: complete (90-100% progress)
        setConversionProgress(100);
        setUploadProgress(100);

        const result: VideoUploadResult = {
          gif_key: gifKey,
          original_filename: file.name,
        };

        onSuccess?.(result);
        toast.success(`uploaded and converted video ${file.name} successfully`);

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : err
              ? String(err)
              : "upload failed - unknown error";
        console.error("Video upload error:", err);
        setError(errorMessage);
        onError?.(errorMessage);
        toast.error(`Video upload failed: ${errorMessage}`);
        throw err;
      } finally {
        setIsUploading(false);
        setTimeout(() => {
          setConversionProgress(0);
          setUploadProgress(0);
        }, 1000);
      }
    },
    [validateFile, uploadToConvex, reffId, onSuccess, onError]
  );

  const reset = useCallback(() => {
    setError(null);
    setConversionProgress(0);
    setUploadProgress(0);
    setIsUploading(false);
  }, []);

  return {
    upload,
    isUploading,
    conversionProgress,
    uploadProgress,
    error,
    reset,
  };
}
